import { useMemo, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { LIST_PAGE_SHELL } from "@/components/layout/pageShell";
import { useDocTypeListWithPagination } from "@/features/common/api/useDocTypeListWithPagination";
import { useURLFilters } from "@/lib/hooks/useURLFilters";
import { useStandardListConfig } from "./useStandardListConfig";
import { ListQuickFiltersBar } from "@/components/list/ListQuickFiltersBar";
import { useStoreList, type StorefrontEntry } from "@/hooks/useStoreList";
import { useFilterStore } from "@/lib/store/filterStore";
import { convertListViewFiltersToFilterDescriptors } from "@/lib/views/utils/buildListColumnsFromSchema";
import { buildNewRoute } from "@/lib/config/docRoutes";
import { safeDecodeURIComponent } from "@/lib/utils";
import { GenericInlineDetailPanel } from "./GenericInlineDetailPanel";
import { GenericDetailPanel } from "./GenericDetailPanel";
import { useDetailPanelState } from "./useDetailPanelState";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { buildDetailRoute } from "@/lib/config/docRoutes";
import type { ListViewFilter } from "@/lib/views/listViewSchemas/types";
import type { FilterDescriptor } from "@/lib/erp";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";
import type { ColumnDef } from "@tanstack/react-table";
import { List, Download, MoreHorizontal, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useLanguage } from "@/i18n/useLanguage";
import { SimpleTableView, type SimpleTableColumn, type SimpleTableStat } from "@/features/common/components/SimpleTableView";
import { OrderCardList } from "@/features/orders/components/OrderCardList";
import { BookingCardList, computeBookingSummaryStats } from "@/features/booking/components/BookingCardList";
import { OrderStatusBadge } from "@/features/orders/components/OrderStatusBadge";
import { ChannelBadge } from "@/features/channels/components/ChannelBadge";
import { formatDateTime } from "@/lib/format";
import { PersonalizationExportMenu } from "@/features/orders/components/PersonalizationExportMenu";
import { ExportDialog } from "@/features/common/components/ExportDialog";
import { ShowArchivedToggle } from "@/components/list/ShowArchivedToggle";
import { useArchivedCount } from "@/features/common/api/useArchivedCount";

type StandardListPageProps = {
  forcedDoctype?: string; // For mapped "pretty" routes like /orders
};

/**
 * Merge shell's effective fields with hook fields (additionalFields + dot-notation titles).
 * The shell computes visible+technical fields but doesn't know about:
 *   - Link title fields (e.g. "company.tenant_display_name")
 *   - additionalFields from the schema (e.g. per_delivered, shopee_order_status)
 * Both must be included in the API request.
 */
function mergeFieldsWithDotNotation(
  shellFields: string[] | undefined,
  hookFields: string[],
): string[] {
  if (!shellFields?.length) return hookFields;
  return Array.from(new Set([...shellFields, ...hookFields]));
}

function getCanonicalFilterStoreKey(doctype: string): string {
  const normalized = doctype.toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  return `list.${normalized}`;
}

// ---------------------------------------------------------------------------
// Simple-view column definitions (per-doctype)
// ---------------------------------------------------------------------------

/** Helper — accessor for generic Record rows */
const rowField = (field: string) => (row: RowWithId) => row[field];

function buildSalesOrderSimpleColumns(language: string): SimpleTableColumn<RowWithId>[] {
  const th = language === "th";

  /** Compact relative date — "2h ago", "Yesterday", "Jun 12" */
  const formatCompactDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 60) return th ? `${diffMin} นาทีที่แล้ว` : `${diffMin}m ago`;
    if (diffHr < 24) return th ? `${diffHr} ชม.ที่แล้ว` : `${diffHr}h ago`;
    if (diffDay === 1) return th ? "เมื่อวาน" : "Yesterday";
    if (diffDay < 7) return th ? `${diffDay} วันที่แล้ว` : `${diffDay}d ago`;
    return date.toLocaleDateString(th ? "th-TH" : "en-US", { month: "short", day: "numeric" });
  };

  return [
    {
      id: "order_info",
      accessor: rowField("name"),
      header: th ? "คำสั่งซื้อ" : "Order",
      cell: (row) => {
        const orderId = String(row.name ?? "");
        const customer = String(row.customer_name ?? "");
        const date = row.creation ? formatCompactDate(String(row.creation)) : "";
        return (
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-[13px] text-foreground truncate">{customer || orderId}</span>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="font-mono">{orderId}</span>
              <span>·</span>
              <span>{date}</span>
            </div>
          </div>
        );
      },
    },
    {
      id: "total_qty",
      accessor: rowField("total_qty"),
      header: th ? "รายการ" : "Items",
      width: "90px",
      cell: (_row, value) => {
        const qty = Number(value ?? 0);
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Package className="w-3.5 h-3.5 shrink-0 opacity-50" />
            <span className="text-[13px] tabular-nums">{qty}</span>
          </div>
        );
      },
    },
    {
      id: "status",
      accessor: rowField("status"),
      header: th ? "สถานะ" : "Status",
      width: "140px",
      cell: (row) => {
        const status = (row.status ?? row.docstatus) as string;
        const perDelivered = row.per_delivered != null ? Number(row.per_delivered) : undefined;
        const marketplaceStatus = (row.shopee_order_status ?? row.lazada_order_status ?? undefined) as string | undefined;
        return <OrderStatusBadge status={status} perDelivered={perDelivered} marketplaceStatus={marketplaceStatus} />;
      },
    },
    {
      id: "grand_total",
      accessor: rowField("grand_total"),
      header: th ? "ยอดรวม" : "Total",
      align: "right" as const,
      width: "120px",
      cell: (_row, value) => {
        const num = Number(value ?? 0);
        return (
          <span className="font-semibold text-[13px] tabular-nums">
            ฿{num.toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        );
      },
    },
    {
      id: "source_channel",
      accessor: rowField("source_channel"),
      header: th ? "ช่องทาง" : "Channel",
      width: "100px",
      hideBelow: "lg" as const,
      cell: (_row, value) => {
        const channel = (value ?? "storefront") as string;
        return <ChannelBadge channel={channel} />;
      },
    },
  ];
}
function buildDefaultSimpleColumns(language: string): SimpleTableColumn<RowWithId>[] {
  const th = language === "th";
  return [
    {
      id: "name",
      accessor: rowField("name"),
      header: th ? "ชื่อ" : "Name",
      cell: (row) => <span className="font-medium text-foreground truncate">{String(row.name ?? "")}</span>,
    },
    {
      id: "status",
      accessor: rowField("status"),
      header: th ? "สถานะ" : "Status",
      width: "140px",
      cell: (_row, value) => <span className="text-muted-foreground">{String(value ?? "—")}</span>,
    },
    {
      id: "modified",
      accessor: rowField("modified"),
      header: th ? "แก้ไขล่าสุด" : "Modified",
      width: "160px",
      cell: (_row, value) => (
        <span className="text-muted-foreground text-xs">
          {value ? formatDateTime(value as string) : "—"}
        </span>
      ),
    },
  ];
}

function buildBookingResourceConfigSimpleColumns(language: string): SimpleTableColumn<RowWithId>[] {
  const th = language === "th";
  return [
    {
      id: "config_name",
      header: th ? "ชื่อ Config" : "Config Name",
      accessor: rowField("config_name"),
      cell: (row, value) => (
        <div className="min-w-0">
          <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{String(value ?? row.name ?? "")}</span>
        </div>
      ),
    },
    {
      id: "slot_duration_minutes",
      header: th ? "ระยะเวลาสล็อต" : "Slot Duration",
      accessor: rowField("slot_duration_minutes"),
      cell: (_row, value) => {
        const mins = Number(value);
        if (!mins) return <span className="text-neutral-300 dark:text-neutral-700">—</span>;
        return <span className="font-mono text-[12px] text-neutral-500 dark:text-neutral-400">{mins >= 60 ? `${mins / 60} hr` : `${mins} min`}</span>;
      },
      width: "120px",
    },
    {
      id: "max_concurrent_bookings",
      header: th ? "จองพร้อมกัน" : "Max Concurrent",
      accessor: rowField("max_concurrent_bookings"),
      cell: (_row, value) => {
        const n = Number(value);
        return <span className="tabular-nums font-medium text-neutral-700 dark:text-neutral-300">{n || 1}</span>;
      },
      align: "right",
      width: "130px",
      hideBelow: "md",
    },
    {
      id: "advance_booking_days",
      header: th ? "จองล่วงหน้า" : "Advance Booking",
      accessor: rowField("advance_booking_days"),
      cell: (_row, value) => {
        const days = Number(value);
        if (!days) return <span className="text-neutral-300 dark:text-neutral-700">—</span>;
        return <span className="font-mono text-[12px] text-neutral-500 dark:text-neutral-400">{days} {th ? "วัน" : "days"}</span>;
      },
      width: "130px",
      hideBelow: "lg",
    },
    {
      id: "status",
      header: th ? "สถานะ" : "Status",
      accessor: rowField("status"),
      cell: (_row, value) => <span className="text-muted-foreground">{String(value ?? "—")}</span>,
      width: "100px",
    },
  ];
}

function computeBookingResourceConfigStats(items: RowWithId[]): SimpleTableStat[] {
  let active = 0;
  let draft = 0;
  for (const row of items) {
    const s = String(row.status ?? "");
    if (s === "Active") active++;
    else if (s === "Draft") draft++;
  }
  const stats: SimpleTableStat[] = [];
  if (active > 0) stats.push({ label: "Active", value: active, color: "green" });
  if (draft > 0) stats.push({ label: "Draft", value: draft, color: "orange" });
  return stats;
}

function computeOrderSummaryStats(items: RowWithId[]): SimpleTableStat[] {
  let processing = 0;
  let readyToShip = 0;
  let completed = 0;
  for (const row of items) {
    const status = String(row.status ?? "").toLowerCase();
    if (status === "to deliver and bill" || status === "to bill") processing++;
    else if (status === "to deliver") readyToShip++;
    else if (status === "completed") completed++;
  }
  const stats: SimpleTableStat[] = [];
  if (processing > 0) stats.push({ label: "Processing", value: processing, color: "orange" });
  if (readyToShip > 0) stats.push({ label: "Ready to ship", value: readyToShip, color: "green" });
  if (completed > 0) stats.push({ label: "Completed", value: completed, color: "green" });
  return stats;
}

export function StandardListPage({ forcedDoctype }: StandardListPageProps) {
  const params = useParams<{ doctype: string }>();
  const [searchParams] = useSearchParams();
  const doctype = forcedDoctype ?? safeDecodeURIComponent(params.doctype ?? "");

  if (!doctype) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Missing doctype parameter</div>
      </div>
    );
  }

  return <StandardListPageInner doctype={doctype} searchParams={searchParams} />;
}

// Minimal row type
type RowWithId = Record<string, unknown> & {
  name?: string;
  id?: string;
  _id?: string;
};

function StandardListPageInner(props: { doctype: string; searchParams: URLSearchParams }) {
  const { doctype, searchParams } = props;
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Simple view columns — per-doctype rich columns or generic fallback
  const simpleColumns = useMemo<SimpleTableColumn<RowWithId>[]>(() => {
    if (doctype === "Sales Order") return buildSalesOrderSimpleColumns(language);
    if (doctype === "Booking Resource Config") return buildBookingResourceConfigSimpleColumns(language);
    return buildDefaultSimpleColumns(language);
  }, [doctype, language]);

  const computeSummaryStats = useCallback((items: RowWithId[]): SimpleTableStat[] => {
    if (doctype === "Sales Order") return computeOrderSummaryStats(items);
    if (doctype === "Booking") return computeBookingSummaryStats(items);
    if (doctype === "Booking Resource Config") return computeBookingResourceConfigStats(items);
    return [];
  }, [doctype]);

  const filterStoreKey = getCanonicalFilterStoreKey(doctype);

  // --- Inline detail panel state ---
  const [inlineDetailDocName, setInlineDetailDocName] = useState<string | null>(null);
  const handleInlineClose = useCallback(() => setInlineDetailDocName(null), []);

  // --- Slide-over / modal / fullscreen detail panel state (with URL sync) ---
  const {
    detailPanelItem, panelItem, openDetailPanel, closeDetailPanel, setDetailPanelItem,
  } = useDetailPanelState();

  const isDesktop = useMediaQuery("(min-width: 768px)");

  // On mobile, redirect ?detail= URL to full-page route (e.g. shared links)
  useEffect(() => {
    if (isDesktop) return;
    if (detailPanelItem?.name) {
      setDetailPanelItem(null);
      navigate(buildDetailRoute(doctype, detailPanelItem.name), { replace: true });
    }
  }, [isDesktop, detailPanelItem, doctype, navigate, setDetailPanelItem]);

  // On mobile, navigate to full-page detail route instead of opening panel
  const handleOpenDetail = useCallback((name: string) => {
    if (isDesktop) {
      openDetailPanel(name);
    } else {
      navigate(buildDetailRoute(doctype, name));
    }
  }, [isDesktop, openDetailPanel, doctype, navigate]);

  // Auto-close inline detail on filter/search change.
  // Use a settling timer instead of a simple mounted boolean — quick filter initialization
  // fires asynchronously after mount (setting filters in the store), which would clear
  // a ?detail= panel opened via URL. 500ms covers the init window.
  const settledRef = useRef(false);
  useEffect(() => {
    const timer = setTimeout(() => { settledRef.current = true; }, 500);
    return () => clearTimeout(timer);
  }, []);

  const searchParamsKey = useMemo(() => {
    const filtered = new URLSearchParams(searchParams);
    filtered.delete("detail"); // Exclude detail param so opening panel doesn't auto-close inline detail
    return filtered.toString();
  }, [searchParams]);

  const prevSearchParamsKeyRef = useRef(searchParamsKey);
  useEffect(() => {
    if (prevSearchParamsKeyRef.current === searchParamsKey) return;
    prevSearchParamsKeyRef.current = searchParamsKey;
    if (!settledRef.current) return;
    if (inlineDetailDocName) setInlineDetailDocName(null);
    if (detailPanelItem) setDetailPanelItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  const filters = useFilterStore(
    useCallback((s) => s.scopedFilters[filterStoreKey], [filterStoreKey]),
  );
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    if (prevFiltersRef.current === filters) { prevFiltersRef.current = filters; return; }
    prevFiltersRef.current = filters;
    if (!settledRef.current) return;
    if (inlineDetailDocName) setInlineDetailDocName(null);
    if (detailPanelItem) setDetailPanelItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Focus management: restore focus on panel close
  const lastOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    if (inlineDetailDocName) {
      lastOpenedRef.current = inlineDetailDocName;
    } else if (lastOpenedRef.current) {
      const rowEl = document.querySelector(
        `[data-row-id="${CSS.escape(lastOpenedRef.current)}"]`,
      ) as HTMLElement | null;
      rowEl?.focus();
      lastOpenedRef.current = null;
    }
  }, [inlineDetailDocName]);

  const hasUrlFilters = useMemo(() => {
    return (
      searchParams.has("filters") ||
      searchParams.has("filters_token") ||
      searchParams.has("filters_key")
    );
  }, [searchParams.toString()]);

  const {
    columns,
    defaultSorting,
    fields,
    quickFilters: baseQuickFilters,
    storefrontFilterField,
    listSettingsConfig,
    exportExtraFields,
  } = useStandardListConfig({ doctype, filterStoreKey });

  // Multi-store separation: when this DocType carries a storefront-source field
  // and the tenant has 2+ storefronts, append one quick-filter tab per store so
  // a merchant can view a single brand's orders. Single-store tenants (or
  // DocTypes without the opt-in field) get the schema's tabs unchanged.
  const { data: storeList } = useStoreList();
  const quickFilters = useMemo(() => {
    const base = baseQuickFilters ?? [];
    if (!storefrontFilterField || !storeList || storeList.length < 2) return base;
    const storeTabs = storeList.map((store: StorefrontEntry) => ({
      id: `store:${store.storefront_key}`,
      label: store.label,
      filters: [[storefrontFilterField, "=", store.storefront_key]] as ListViewFilter[],
    }));
    return [...base, ...storeTabs];
  }, [baseQuickFilters, storefrontFilterField, storeList]);

  useURLFilters(filterStoreKey, doctype);

  // Columns — row click handles navigation, no separate actions column needed
  const columnsForShell = useMemo(() => {
    return (ctx: {
      items: RowWithId[];
      visibleFieldOrder?: string[];
      availableFields?: Array<{ fieldname: string; label: string; fieldtype?: string }>;
    }) => {
      return columns(ctx as any) as ColumnDef<RowWithId, unknown>[];
    };
  }, [columns]);

  // renderSubRow for inline detail panel
  const renderSubRow = useCallback(
    (row: RowWithId, columnInfo: SubRowColumnInfo): ReactNode | null => {
      const rowName = (row.name ?? row.id ?? row._id) as string | undefined;
      if (!rowName || rowName !== inlineDetailDocName) return null;
      return (
        <GenericInlineDetailPanel
          key={rowName}
          doctype={doctype}
          docName={rowName}
          rowData={row}
          onClose={handleInlineClose}
          columns={columnInfo}
        />
      );
    },
    [doctype, inlineDetailDocName, handleInlineClose],
  );

  // Quick filter state
  const [activeQuickFilterId, setActiveQuickFilterId] = useState<string | null>(null);

  useEffect(() => {
    if (hasUrlFilters) return;
    if (!quickFilters?.length) return;
    if (activeQuickFilterId !== null) return;
    const defaultFilter = quickFilters.find((f) => f.default);
    if (defaultFilter) setActiveQuickFilterId(defaultFilter.id);
  }, [hasUrlFilters, quickFilters, activeQuickFilterId]);

  const handleQuickFilterChange = useCallback(
    (nextId: string | null, pillFilters: ListViewFilter[]) => {
      setActiveQuickFilterId(nextId);
      if (nextId === null) {
        useFilterStore.getState().setFiltersForKey(filterStoreKey, []);
        return;
      }
      const filterDescriptors = convertListViewFiltersToFilterDescriptors(pillFilters, doctype);
      useFilterStore.getState().setFiltersForKey(filterStoreKey, filterDescriptors);
    },
    [doctype, filterStoreKey]
  );

  useEffect(() => {
    if (hasUrlFilters) return;
    if (!quickFilters?.length || activeQuickFilterId === null) return;
    const currentFilters = useFilterStore.getState().scopedFilters[filterStoreKey];
    if (currentFilters?.length) return;
    const activeFilter = quickFilters.find((f) => f.id === activeQuickFilterId);
    if (!activeFilter) return;
    const filterDescriptors = convertListViewFiltersToFilterDescriptors(activeFilter.filters, doctype);
    useFilterStore.getState().setFiltersForKey(filterStoreKey, filterDescriptors);
  }, [hasUrlFilters, quickFilters, activeQuickFilterId, filterStoreKey, doctype]);

  // Row ID extraction
  const getDocName = useCallback((row: RowWithId): string | null => {
    return (row?.name as string | undefined) ??
      (row?.id as string | undefined) ??
      (row?._id as string | undefined) ??
      null;
  }, []);

  // Archive toggle state
  const showArchived = usePreferencesStore(
    useCallback((s: { showArchived: Record<string, boolean> }) => s.showArchived[doctype] ?? false, [doctype]),
  );
  const { data: archivedData } = useArchivedCount(doctype);
  const disabledFieldName = archivedData?.disabled_field_name ?? null;

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const handleNewClick = useCallback(() => {
    navigate(buildNewRoute(doctype));
  }, [doctype, navigate]);

  const getRowId = useCallback(
    (row: RowWithId): string => {
      const docName = getDocName(row);
      if (!docName) {
        const sortedEntries = Object.entries(row)
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(0, 5);
        const hashContent = sortedEntries
          .map(([key, value]) => `${key}:${String(value).substring(0, 20)}`)
          .join("|");
        let hash = 0;
        for (let i = 0; i < hashContent.length; i++) {
          const char = hashContent.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        return `unknown-${Math.abs(hash).toString(36)}`;
      }
      return docName;
    },
    [getDocName]
  );

  function useListHook(params: {
    filterDescriptors: FilterDescriptor[];
    searchQuery: string;
    pageIndex: number;
    pageSize: number;
    orderBy?: string;
    fields?: string[];
  }) {
    // Inject disabled=0 filter when showArchived is OFF (only if doctype has a disabled field)
    const archiveFilter: FilterDescriptor[] = (showArchived || !disabledFieldName)
      ? []
      : [{ field: disabledFieldName, operator: "equals" as const, value: "0" }];

    // Ensure disabled field is fetched for row styling (only if it exists)
    const mergedFields = mergeFieldsWithDotNotation(params.fields, fields);
    const fieldsWithDisabled = !disabledFieldName || mergedFields.includes(disabledFieldName)
      ? mergedFields
      : [...mergedFields, disabledFieldName];

    return useDocTypeListWithPagination<Record<string, unknown>>({
      doctype,
      filterDescriptors: [...archiveFilter, ...params.filterDescriptors],
      searchQuery: params.searchQuery,
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      orderBy: params.orderBy,
      fields: fieldsWithDisabled,
    });
  }

  return (
    <div className={LIST_PAGE_SHELL}>
      <div>
        <DocTypeListPageShell
          doctype={doctype}
          showArchived={showArchived}
          columns={columnsForShell}
          filterStoreKey={filterStoreKey}
          useListHook={useListHook}
          defaultSorting={defaultSorting}
          pageSizeOptions={[20, 50, 100, 200, 400, 500]}
          title={doctype}
          searchPlaceholder={`Search ${doctype}...`}
          newButtonLabel={`New ${doctype}`}
          onNewClick={handleNewClick}
          onRowClick={(row) => {
            const name = (row as Record<string, unknown>).name as string | undefined;
            if (name) handleOpenDetail(name);
          }}
          onSearchSelect={handleOpenDetail}
          renderSubRow={renderSubRow}
          activeSubRowId={inlineDetailDocName ?? undefined}
          getRowId={getRowId}
          headerBelow={
            <div className="flex items-center gap-2 flex-wrap">
              {quickFilters && quickFilters.length > 0 && (
                <ListQuickFiltersBar
                  doctype={doctype}
                  quickFilters={quickFilters}
                  activeFilterId={activeQuickFilterId}
                  onChange={handleQuickFilterChange}
                />
              )}
              <ShowArchivedToggle doctype={doctype} />
            </div>
          }
          listSettingsConfig={listSettingsConfig}
          inlineEditable
          isInlineEditMode
          compactMode
          noPadding
          fullWidth
          className="pt-0"
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          breadcrumbs={[
            {
              label: doctype,
              icon: (
                <span className="flex items-center justify-center h-5 w-5 rounded-xs bg-neutral-500 shrink-0">
                  <List className="h-3 w-3 text-white" />
                </span>
              ),
            },
          ]}
          enableViewModeSwitch={true}
          enableSimpleView
          renderSimpleView={({ items: simpleItems, isLoading: simpleLoading, total: simpleTotal, refetch: simpleRefetch }) =>
            doctype === "Sales Order" ? (
              <OrderCardList
                items={simpleItems}
                isLoading={simpleLoading}
                total={simpleTotal}
                getRowId={getRowId}
                onRowClick={(id) => handleOpenDetail(id)}
                summaryStats={computeSummaryStats(simpleItems)}
              />
            ) : doctype === "Booking" ? (
              <BookingCardList
                items={simpleItems}
                isLoading={simpleLoading}
                total={simpleTotal}
                getRowId={getRowId}
                onRowClick={(id) => handleOpenDetail(id)}
                summaryStats={computeBookingSummaryStats(simpleItems)}
                onRefresh={simpleRefetch}
              />
            ) : (
              <SimpleTableView<RowWithId>
                items={simpleItems}
                columns={simpleColumns}
                isLoading={simpleLoading}
                total={simpleTotal}
                getRowId={getRowId}
                onRowClick={(id) => handleOpenDetail(id)}
                summaryStats={computeSummaryStats(simpleItems)}
                minRowHeight={60}
              />
            )
          }
          headerActions={(table, context) => {
            const selected = table.getSelectedRowModel().rows
              .map((r) => r.original?.name as string | undefined)
              .filter(Boolean) as string[];
            return (
              <div className="flex items-center gap-2">
                {doctype === "Sales Order" ? (
                  <PersonalizationExportMenu
                    selectedOrders={selected}
                    onExport={() => setExportDialogOpen(true)}
                  />
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="shrink-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <ExportDialog
                  open={exportDialogOpen}
                  onOpenChange={setExportDialogOpen}
                  doctype={doctype}
                  availableFields={context?.listSettings?.availableFields ?? []}
                  visibleFieldOrder={context?.listSettings?.visibleFieldOrder ?? []}
                  extraFields={exportExtraFields}
                  filterStoreKey={filterStoreKey}
                  selectedRowNames={selected}
                />
              </div>
            );
          }}
        />
      </div>

      {/* Panel only renders on desktop — mobile navigates to full-page route */}
      {isDesktop && (
        <GenericDetailPanel
          open={!!detailPanelItem}
          onClose={closeDetailPanel}
          doctype={doctype}
          docName={panelItem?.name ?? ""}
          docLabel={panelItem?.label}
          wide={doctype === "Sales Order" || doctype === "Booking"}
        />
      )}
    </div>
  );
}
